import React, { useState } from 'react';
import { gql } from '@apollo/client';
import {
  BooleanInput,
  NumberInput,
  SelectInput,
  Datagrid,
  FunctionField,
  useRecordContext,
  useDataProvider,
} from 'react-admin';
import { Box, Button, Chip, Link } from '@mui/material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import List from 'components/List';
import MarketInput from 'components/MarketInput';
import PositionInput from 'components/PositionInput';
import DateFilter from 'components/Filter/DateFilter';
import LinkField from 'components/LinkField';
import MoneyField from 'components/MoneyField';
import PercentField from 'components/PercentField';
import ApprovalConfirmDialog from './ApprovalConfirmDialog';
import { titleize } from 'inflection';
import { RejectGeneratedRateModal } from './RejectGeneratedRateModal';

const statusChoices = [
  { id: 'pending_review', name: 'Pending Review' },
  { id: 'approved', name: 'Approved' },
  { id: 'auto_approved', name: 'Auto Approved' },
  { id: 'diff_too_low', name: 'Diff too Low' },
  { id: 'rejected', name: 'Rejected' },
  { id: 'expired', name: 'Expired' },
];

const GeneratedRatesListFilters = [
  <MarketInput key="market" alwaysOn resettable source="search.scope_id" />,
  <PositionInput key="position" alwaysOn emptyText="All" source="search.position_id" />,
  <BooleanInput
    key="show_only_latest_rate"
    label="Show Only Latest Rate"
    source="search.show_only_latest_rate"
    alwaysOn
  />,
  <SelectInput
    key="status"
    label="Status"
    source="search.status"
    choices={statusChoices}
    emptyText="All"
    alwaysOn
  />,
  <NumberInput
    key="min_variance"
    label="Min. Variance %"
    source="search.minimum_variance_percentage"
  />,
  <DateFilter key="last_updated" label="Last Updated" source="search.updated_at_lte" alwaysOn />,
  <DateFilter key="deadline" label="Deadline until" source="search.review_deadline" alwaysOn />,
];

const CurrentRateLink = ({ record }) => {
  const dataProvider = useDataProvider();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const marketId = record?.scope_id;
  const positionId = record?.position_id;
  const specialtyId = record?.specialty_id;

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (!marketId || !positionId) {
      return;
    }

    setIsLoading(true);

    try {
      const { data } = await dataProvider.getManyReference('MarketPositionRateSetting', {
        target: 'market_id',
        id: marketId,
        pagination: { page: 1, perPage: 2 },
        sort: { field: 'id', order: 'DESC' },
        filter: {
          market_id: marketId,
          position_id: positionId,
          ...(specialtyId && { specialty_id: specialtyId }),
        },
      });

      if (data && data[0]?.id) {
        navigate(`/MarketPositionRateSetting/${data[0].id}/show`);
      }
    } catch (error) {
      console.error('Error fetching market position rate setting:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Link
      component="button"
      onClick={handleClick}
      sx={{
        cursor: isLoading ? 'wait' : 'pointer',
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      <MoneyField
        source="current_rate_cents"
        record={{ current_rate_cents: record.current_rate_cents }}
        blankIfNull
      />
    </Link>
  );
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) {
    return '';
  }
  try {
    return format(new Date(dateString), 'MM/dd/yyyy');
  } catch {
    return '';
  }
};

const formatTimeToFill = (hours: number | null | undefined): string => {
  if (!hours && hours !== 0) {
    return '';
  }
  if (hours < 24) {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours} hours ${minutes ? `and ${minutes} minutes` : ''}`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = Math.floor(hours % 24);
  const minutes = Math.round((hours % 1) * 60);
  return `${days} days, ${remainingHours} hours ${minutes ? `and ${minutes} minutes` : ''}`;
};

const ActionButtons = () => {
  const record = useRecordContext();

  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

  if (!record) {
    return null;
  }

  const isPendingReview = record.status === 'pending_review';

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          variant="contained"
          color="success"
          onClick={() => setApprovalDialogOpen(true)}
          disabled={!isPendingReview}
        >
          Approve
        </Button>
        <Button
          size="small"
          variant="contained"
          color="error"
          onClick={() => setIsRejectModalOpen(true)}
          disabled={!isPendingReview}
        >
          Reject
        </Button>
      </Box>
      <ApprovalConfirmDialog
        open={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
        record={record}
      />
      <RejectGeneratedRateModal
        record={record}
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
      />
    </>
  );
};

const GeneratedRatesList = () => {
  const statusColor = (status: string) => {
    switch (status) {
      case 'pending_review':
        return 'warning';
      case 'approved':
        return 'success';
      case 'auto_approved':
        return 'info';
      case 'diff_too_low':
        return 'error';
      case 'rejected':
        return 'error';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <List
      filters={GeneratedRatesListFilters}
      exporter={false}
      perPageOptions={[25, 50, 100, 150, 250]}
      filterDefaultValues={{ search: { show_only_latest_rate: true } }}
    >
      <Datagrid bulkActionButtons={false}>
        <LinkField label="Market" source="market.display_name" sortBy="scope_name" />
        <LinkField label="Position" source="position.display_name" sortBy="position_name" />
        <LinkField label="Specialty" source="specialty.label" sortBy="specialty_name" />
        <MoneyField label="New Rate" source="generated_rate_cents" blankIfNull />
        <FunctionField
          label="Current rate"
          render={(record) => {
            return <CurrentRateLink record={record} />;
          }}
        />
        <PercentField label="Variance" source="variance_percent" sortBy="variance" />
        <FunctionField
          label="Date Generated"
          render={(record) => formatDate(record?.created_at)}
          sortBy="created_at"
        />
        <FunctionField
          label="Deadline"
          render={(record) => formatDate(record?.review_deadline)}
          sortBy="review_deadline"
        />
        <FunctionField
          label="Status"
          sortBy="status"
          render={(record) => {
            return (
              <Box sx={{ minWidth: 110 }}>
                <Chip
                  color={statusColor(record?.status)}
                  label={titleize(record?.status)}
                  size="small"
                />
              </Box>
            );
          }}
        />
        <FunctionField label="Actions" render={() => <ActionButtons />} />

        <FunctionField
          label="Last Approved"
          render={(record) => formatDate(record?.last_approved?.approved_at)}
          sortBy="last_approved_at"
        />
        <FunctionField
          label="Last Approved Type"
          render={(record) => record?.last_approved?.type || ''}
          sortBy="last_approved_type"
        />
        <FunctionField
          label="GSV (L3M)"
          render={(record) => {
            const gsv = record?.metrics_data?.gsv_cents_last_3_months;
            if (gsv === null || gsv === undefined) {
              return '';
            }
            return <MoneyField source="gsv" record={{ gsv }} blankIfNull />;
          }}
          sortBy="metrics_data_gsv_cents_last_3_months"
        />
        <PercentField
          label="Fill Rate (L3M)"
          source="metrics_data.fill_rate_percentage_last_3_months"
          sortBy="metrics_data_fill_rate_percentage_last_3_months"
        />
        <FunctionField
          label="Supply Level Category"
          render={(record) => record?.metrics_data?.supply_level_category || ''}
          sortBy="metrics_data_supply_level_category"
        />
        <FunctionField
          label="Supply Utilization Category"
          render={(record) => record?.metrics_data?.supply_utilization_category || ''}
          sortBy="metrics_data_supply_utilization_category"
        />
        <FunctionField
          label="Time to Fill"
          render={(record) => (
            <Box sx={{ minWidth: 110 }}>
              {formatTimeToFill(record?.metrics_data?.hours_to_fill_last_3_months)}
            </Box>
          )}
          sortBy="metrics_data_hours_to_fill_last_3_months"
        />
      </Datagrid>
    </List>
  );
};

GeneratedRatesList.query = gql`
  query autoGeneratedRates($input: AutoGeneratedRateQueryInput) {
    _autoGeneratedRatesMeta(input: $input) {
      count
    }
    autoGeneratedRates(input: $input) {
      id
      status
      review_deadline
      created_at
      generated_rate_cents
      current_rate_cents
      variance_percent
      scope_type
      scope_id
      market {
        id
        display_name
      }
      position_id
      position {
        id
        display_name
      }
      specialty_id
      specialty {
        id
        label
      }
      last_approved {
        approved_at
        type
      }
      metrics_data {
        gsv_cents_last_3_months
        fill_rate_percentage_last_3_months
        supply_level_category
        supply_utilization_category
        hours_to_fill_last_3_months
      }
    }
  }
`;

export default GeneratedRatesList;
